/**
 * ChannelPackageEditModal Component
 *
 * Modal zum Bearbeiten von Channel Package Bildern.
 * Erlaubt das Ersetzen, Hinzufügen und Löschen von Bildern.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState, useCallback, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/v2/ui/Dialog';
import { Button, Input } from '@/components/v2/ui';
import { getAssetThumbnailUrl } from '@/lib/helpers/asset-urls';
import type { ChannelPackage, AssetRendition } from '@/types/channel-packages';
import { Upload, Trash2, Plus, Save } from 'lucide-react';
import apiClient from '@/lib/api-client';

interface ChannelPackageEditModalProps {
  package_: ChannelPackage | null;
  isOpen: boolean;
  onClose: () => void;
}

interface ImageEditState {
  rendition: AssetRendition;
  newFile?: File;
  altText: string;
  isDeleted: boolean;
}

export function ChannelPackageEditModal({
  package_: pkg,
  isOpen,
  onClose,
}: ChannelPackageEditModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});

  // Local state for editing
  const [imageStates, setImageStates] = useState<{ [slot: number]: ImageEditState }>({});
  const [newImages, setNewImages] = useState<Array<{ file: File; altText: string; tempId: string }>>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Get data (may be null)
  const version = pkg?.current_version;
  const assetRenditions = useMemo(() => version?.asset_renditions || [], [version?.asset_renditions]);

  // Initialize image states
  const getImageState = (rendition: AssetRendition): ImageEditState => {
    if (!imageStates[rendition.slot]) {
      return {
        rendition,
        altText: rendition.alt_text || '',
        isDeleted: false,
      };
    }
    return imageStates[rendition.slot];
  };

  // Handle file selection for replacement
  const handleFileSelect = useCallback((slot: number, file: File | undefined) => {
    if (!file) return;

    const rendition = assetRenditions.find(r => r.slot === slot);
    if (!rendition) return;

    setImageStates(prev => ({
      ...prev,
      [slot]: {
        rendition,
        altText: prev[slot]?.altText || rendition.alt_text || '',
        isDeleted: prev[slot]?.isDeleted || false,
        newFile: file,
      },
    }));
  }, [assetRenditions]);

  // Handle alt text change
  const handleAltTextChange = useCallback((slot: number, altText: string) => {
    const rendition = assetRenditions.find(r => r.slot === slot);
    if (!rendition) return;

    setImageStates(prev => ({
      ...prev,
      [slot]: {
        rendition,
        altText,
        isDeleted: prev[slot]?.isDeleted || false,
        newFile: prev[slot]?.newFile,
      },
    }));
  }, [assetRenditions]);

  // Handle delete
  const handleDelete = useCallback((slot: number) => {
    const rendition = assetRenditions.find(r => r.slot === slot);
    if (!rendition) return;

    setImageStates(prev => ({
      ...prev,
      [slot]: {
        rendition,
        altText: prev[slot]?.altText || rendition.alt_text || '',
        isDeleted: true,
        newFile: prev[slot]?.newFile,
      },
    }));
  }, [assetRenditions]);

  // Handle undo delete
  const handleUndoDelete = useCallback((slot: number) => {
    const rendition = assetRenditions.find(r => r.slot === slot);
    if (!rendition) return;

    setImageStates(prev => ({
      ...prev,
      [slot]: {
        rendition,
        altText: prev[slot]?.altText || rendition.alt_text || '',
        isDeleted: false,
        newFile: prev[slot]?.newFile,
      },
    }));
  }, [assetRenditions]);

  // Handle add new image
  const handleAddNewImage = useCallback((file: File) => {
    setNewImages(prev => [
      ...prev,
      {
        file,
        altText: '',
        tempId: `new-${Date.now()}-${Math.random()}`,
      },
    ]);
  }, []);

  // Handle save
  const handleSave = async () => {
    if (!pkg || !pkg.product_id || !pkg.channel_id) {
      toast.error(t('channelPackages.missingIds'));
      return;
    }

    setIsSaving(true);

    try {
      // 1. Process deletions
      for (const [slotStr, state] of Object.entries(imageStates)) {
        const slot = parseInt(slotStr);
        if (state.isDeleted) {
          await apiClient.delete(
            `/api/v1/products/${pkg.product_id}/channels/${pkg.channel_id}/images/${slot}`
          );
          toast.success(t('channelPackages.imageDeletedSlot', { slot }));
        }
      }

      // 2. Process replacements
      for (const [slotStr, state] of Object.entries(imageStates)) {
        const slot = parseInt(slotStr);
        if (state.newFile && !state.isDeleted) {
          const formData = new FormData();
          formData.append('file', state.newFile);
          formData.append('alt_text', state.altText);

          await apiClient.put(
            `/api/v1/products/${pkg.product_id}/channels/${pkg.channel_id}/images/${slot}`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          toast.success(t('channelPackages.imageReplacedSlot', { slot }));
        }
      }

      // 3. Process new images
      for (const newImage of newImages) {
        const formData = new FormData();
        formData.append('file', newImage.file);
        formData.append('alt_text', newImage.altText);

        await apiClient.post(
          `/api/v1/products/${pkg.product_id}/channels/${pkg.channel_id}/images`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        toast.success(t('channelPackages.imageAdded'));
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['channel-packages'] });
      queryClient.invalidateQueries({ queryKey: ['channel-package', pkg.channel_id, pkg.id] });

      toast.success(t('toast.success.saved'));
      onClose();
    } catch (error: unknown) {
      console.error('Error saving images:', error);
      const apiError = error as { response?: { data?: { error?: string } } };
      toast.error(apiError.response?.data?.error || t('errors.saving'));
    } finally {
      setIsSaving(false);
    }
  };

  // Don't render if no package
  if (!pkg) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t('channelPackages.editTitle')}
          </DialogTitle>
          <DialogDescription>
            {pkg.channel?.name || t('channelPackages.unknownChannel')} ({pkg.channel?.channel_type})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Existing Images */}
          <div>
            <h3 className="text-sm font-semibold text-neutral-900 mb-3">
              {t('channelPackages.existingImages')}
            </h3>

            {assetRenditions.length === 0 ? (
              <p className="text-sm text-neutral-600">
                {t('channelPackages.noImages')}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {assetRenditions.map((rendition) => {
                  const state = getImageState(rendition);
                  const previewUrl = state.newFile
                    ? URL.createObjectURL(state.newFile)
                    : getAssetThumbnailUrl(rendition);

                  return (
                    <div
                      key={rendition.slot}
                      className={`
                        relative p-4 border rounded-lg
                        ${state.isDeleted
                          ? 'bg-red-50 border-red-300 opacity-50'
                          : 'bg-white border-neutral-200'
                        }
                      `}
                    >
                      {/* Slot Badge */}
                      <div className="absolute top-2 left-2 z-10 bg-black/75 text-white px-2 py-0.5 rounded text-xs font-semibold">
                        Slot #{rendition.slot}
                      </div>

                      {/* Delete Overlay */}
                      {state.isDeleted && (
                        <div className="absolute inset-0 flex items-center justify-center bg-red-500/20 rounded-lg z-10">
                          <div className="text-center">
                            <p className="text-red-700 font-semibold mb-2">
                              {t('channelPackages.markedForDelete')}
                            </p>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleUndoDelete(rendition.slot)}
                            >
                              {t('common.undo')}
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Image Preview */}
                      <div className="aspect-square rounded overflow-hidden bg-neutral-100 mb-3">
                        <img
                          src={previewUrl}
                          alt={state.altText || `Slot ${rendition.slot}`}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Alt Text Input */}
                      <Input
                        label="Alt Text"
                        value={state.altText}
                        onChange={(e) => handleAltTextChange(rendition.slot, e.target.value)}
                        disabled={state.isDeleted}
                        className="mb-3"
                      />

                      {/* Actions */}
                      <div className="flex gap-2">
                        {!state.isDeleted && (
                          <>
                            {/* Replace Button */}
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => fileInputRefs.current[rendition.slot]?.click()}
                              className="flex-1"
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              {t('channelPackages.replaceImage')}
                            </Button>

                            {/* Delete Button */}
                            <Button
                              size="sm"
                              variant="danger"
                              onClick={() => handleDelete(rendition.slot)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}

                        {/* Hidden File Input */}
                        <input
                          ref={(el) => { fileInputRefs.current[rendition.slot] = el; }}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileSelect(rendition.slot, e.target.files?.[0])}
                        />
                      </div>

                      {/* New File Indicator */}
                      {state.newFile && !state.isDeleted && (
                        <p className="text-xs text-green-600 mt-2">
                          {t('products.newImageSelected', { name: state.newFile.name })}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* New Images */}
          {newImages.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-neutral-900 mb-3">
                {t('channelPackages.newImages')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {newImages.map((newImage) => (
                  <div
                    key={newImage.tempId}
                    className="p-4 border border-green-300 bg-green-50 rounded-lg"
                  >
                    {/* Image Preview */}
                    <div className="aspect-square rounded overflow-hidden bg-neutral-100 mb-3">
                      <img
                        src={URL.createObjectURL(newImage.file)}
                        alt={newImage.altText || t('channelPackages.imageAlt')}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Alt Text Input */}
                    <Input
                      label="Alt Text"
                      value={newImage.altText}
                      onChange={(e) => {
                        setNewImages(prev =>
                          prev.map(img =>
                            img.tempId === newImage.tempId
                              ? { ...img, altText: e.target.value }
                              : img
                          )
                        );
                      }}
                      className="mb-3"
                    />

                    <div className="flex justify-between items-center">
                      <p className="text-xs text-neutral-600">
                        {newImage.file.name}
                      </p>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => {
                          setNewImages(prev =>
                            prev.filter(img => img.tempId !== newImage.tempId)
                          );
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add New Image Button */}
          <div>
            <Button
              variant="secondary"
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) handleAddNewImage(file);
                };
                input.click();
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('products.addNewImage')}
            </Button>
          </div>
        </div>

        {/* Footer */}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                {t('common.saving')}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {t('common.save')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
